import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  MapPin, ArrowLeft, Plus, Edit, Trash2, Clock, 
  Banknote, X, Save, ChevronRight
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';

const BusOperatorRoutes = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [routes, setRoutes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [formData, setFormData] = useState({
    origin: { city: '', station: '', country: 'Zambia' },
    destination: { city: '', station: '', country: 'Zambia' },
    stops: [],
    distance_km: '',
    estimated_duration: '',
    base_price: ''
  });
  const [newStop, setNewStop] = useState({ city: '', station: '', arrival_offset: '', stop_duration: 15 });

  useEffect(() => {
    const token = localStorage.getItem('tekstar_token');
    if (!token) {
      navigate('/bus/operator/login');
      return;
    }
    fetchRoutes();
  }, [navigate]);

  const fetchRoutes = async () => {
    try {
      setLoading(true);
      const response = await api.get('/bus/operators/routes');
      setRoutes(response.data.routes || []);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('tekstar_token');
        localStorage.removeItem('tekstar_user');
        navigate('/bus/operator/login');
      } else if (error.response?.status === 404) {
        navigate('/bus/operator/apply');
      } else {
        console.error('Failed to load routes:', error);
        toast({
          title: 'Error',
          description: error.response?.data?.detail || 'Failed to load routes',
          variant: 'destructive'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      ...formData,
      distance_km: parseFloat(formData.distance_km),
      estimated_duration: parseInt(formData.estimated_duration),
      base_price: parseFloat(formData.base_price)
    };

    try {
      if (editingRoute) {
        await api.put(`/bus/operators/routes/${editingRoute.id}`, payload);
        toast({ title: 'Success', description: 'Route updated successfully' });
      } else {
        await api.post('/bus/operators/routes', payload);
        toast({ title: 'Success', description: 'Route created successfully' });
      }
      
      setShowForm(false);
      setEditingRoute(null);
      resetForm();
      fetchRoutes();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save route',
        variant: 'destructive'
      });
    }
  };

  const handleEdit = (route) => {
    setEditingRoute(route);
    setFormData({
      origin: route.origin,
      destination: route.destination,
      stops: route.stops || [],
      distance_km: route.distanceKm?.toString() || '',
      estimated_duration: route.estimatedDuration?.toString() || '',
      base_price: route.basePrice?.toString() || ''
    });
    setShowForm(true);
  };

  const addStop = () => {
    if (!newStop.city || !newStop.arrival_offset) return;
    
    setFormData(prev => ({
      ...prev,
      stops: [...prev.stops, {
        city: newStop.city,
        station: newStop.station || `${newStop.city} Station`,
        arrival_offset: parseInt(newStop.arrival_offset),
        stop_duration: parseInt(newStop.stop_duration) || 15
      }]
    }));
    setNewStop({ city: '', station: '', arrival_offset: '', stop_duration: 15 });
  };

  const removeStop = (index) => {
    setFormData(prev => ({
      ...prev,
      stops: prev.stops.filter((_, i) => i !== index)
    }));
  };

  const resetForm = () => {
    setFormData({
      origin: { city: '', station: '', country: 'Zambia' },
      destination: { city: '', station: '', country: 'Zambia' },
      stops: [],
      distance_km: '',
      estimated_duration: '',
      base_price: ''
    });
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/bus/operator">
                <ArrowLeft className="h-6 w-6" />
              </Link>
              <div>
                <h1 className="text-xl font-bold">Route Management</h1>
                <p className="text-indigo-200 text-sm">{routes.length} route(s)</p>
              </div>
            </div>
            <Button 
              onClick={() => { setShowForm(true); setEditingRoute(null); resetForm(); }}
              className="bg-white text-indigo-600 hover:bg-indigo-50"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Route
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4">
        {/* Add/Edit Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">
                  {editingRoute ? 'Edit Route' : 'Create New Route'}
                </h2>
                <button onClick={() => { setShowForm(false); setEditingRoute(null); }}>
                  <X className="h-6 w-6 text-gray-400" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Origin */}
                <div className="p-4 bg-green-50 rounded-lg">
                  <Label className="flex items-center gap-2 text-green-700 mb-2">
                    <MapPin className="h-4 w-4" />
                    Origin
                  </Label>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      value={formData.origin.city}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        origin: { ...formData.origin, city: e.target.value }
                      })}
                      placeholder="City (e.g., Lusaka)"
                      required
                    />
                    <Input
                      value={formData.origin.station}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        origin: { ...formData.origin, station: e.target.value }
                      })}
                      placeholder="Station name"
                      required
                    />
                  </div>
                </div>

                {/* Stops */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <Label className="mb-2 block">Intermediate Stops (Optional)</Label>
                  
                  {formData.stops.length > 0 && (
                    <div className="space-y-2 mb-3">
                      {formData.stops.map((stop, idx) => (
                        <div key={idx} className="flex items-center gap-2 p-2 bg-white rounded border">
                          <span className="flex-1">{stop.city}</span>
                          <span className="text-sm text-gray-500">+{stop.arrival_offset}min</span>
                          <button type="button" onClick={() => removeStop(idx)}>
                            <X className="h-4 w-4 text-red-500" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="grid grid-cols-4 gap-2">
                    <Input
                      value={newStop.city}
                      onChange={(e) => setNewStop({ ...newStop, city: e.target.value })}
                      placeholder="City"
                      className="col-span-2"
                    />
                    <Input
                      type="number"
                      value={newStop.arrival_offset}
                      onChange={(e) => setNewStop({ ...newStop, arrival_offset: e.target.value })}
                      placeholder="Mins from start"
                    />
                    <Button type="button" onClick={addStop} variant="outline">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Destination */}
                <div className="p-4 bg-red-50 rounded-lg">
                  <Label className="flex items-center gap-2 text-red-700 mb-2">
                    <MapPin className="h-4 w-4" />
                    Destination
                  </Label>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      value={formData.destination.city}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        destination: { ...formData.destination, city: e.target.value }
                      })}
                      placeholder="City (e.g., Livingstone)"
                      required
                    />
                    <Input
                      value={formData.destination.station}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        destination: { ...formData.destination, station: e.target.value }
                      })}
                      placeholder="Station name"
                      required
                    />
                  </div>
                </div>

                {/* Route Details */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="flex items-center gap-1">
                      <MapPin className="h-4 w-4" />
                      Distance (km)
                    </Label>
                    <Input
                      type="number"
                      value={formData.distance_km}
                      onChange={(e) => setFormData({ ...formData, distance_km: e.target.value })}
                      placeholder="475"
                      required
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      Duration (mins)
                    </Label>
                    <Input
                      type="number"
                      value={formData.estimated_duration}
                      onChange={(e) => setFormData({ ...formData, estimated_duration: e.target.value })}
                      placeholder="360"
                      required
                    />
                  </div>
                  <div>
                    <Label className="flex items-center gap-1">
                      <Banknote className="h-4 w-4" />
                      Base Price (K)
                    </Label>
                    <Input
                      type="number"
                      value={formData.base_price}
                      onChange={(e) => setFormData({ ...formData, base_price: e.target.value })}
                      placeholder="250"
                      required
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => { setShowForm(false); setEditingRoute(null); }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-indigo-600 hover:bg-indigo-700">
                    <Save className="h-4 w-4 mr-2" />
                    {editingRoute ? 'Update Route' : 'Create Route'}
                  </Button>
                </div>
              </form>
            </Card>
          </div>
        )}

        {/* Routes List */}
        {routes.length === 0 ? (
          <Card className="p-8 text-center">
            <MapPin className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No Routes Yet</h3>
            <p className="text-gray-500 mb-4">Create your first route to start scheduling trips</p>
            <Button 
              onClick={() => setShowForm(true)}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Your First Route
            </Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {routes.map((route) => (
              <Card key={route.id} className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-lg">{route.origin?.city}</span>
                        <ChevronRight className="h-5 w-5 text-gray-400" />
                        <span className="font-semibold text-lg">{route.destination?.city}</span>
                      </div>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        route.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {route.status}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        {route.distanceKm} km
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {formatDuration(route.estimatedDuration)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Banknote className="h-4 w-4" />
                        K{route.basePrice}
                      </span>
                    </div>

                    {route.stops?.length > 0 && (
                      <div className="mt-2 text-sm text-gray-500">
                        Stops: {route.stops.map(s => s.city).join(' → ')}
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleEdit(route)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BusOperatorRoutes;
